import { useMemo } from 'react';
import type { InfiniteData } from '@tanstack/react-query';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';

export function flattenData<TRes, K extends keyof TRes>(
  data: InfiniteData<TRes | undefined> | undefined,
  dataKey: K,
): NonNullable<TRes[K]> extends Array<infer U> ? U[] : [] {
  type ValueReturnType = NonNullable<TRes[K]> extends Array<infer U> ? U[] : [];

  if (!data?.pages?.length) {
    return STABLE_EMPTY_ARRAY_REF as ValueReturnType;
  }

  return (data.pages as NonNullable<TRes[K]> extends Array<infer U> ? U[] : any[]).reduce((acc, page) => {
    const items = page?.[dataKey];

    // Ensure items exist and are an array before spreading them
    if (Array.isArray(items)) {
      acc.push(...items);
    }

    return acc;
  }, [] as ValueReturnType);
}

export function useFlatData<TRes, K extends keyof TRes>(data: InfiniteData<TRes | undefined> | undefined, dataKey: K) {
  return useMemo(() => flattenData(data, dataKey), [data, dataKey]);
}
